import React, { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import AddCourseIcon from 'icons/addcourse-xl.svg';
import { Trans, useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { z } from 'zod';

import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';
import useValidatedStore from 'components/hooks/useValidatedStore';
import useWindowStorage from 'components/hooks/useWindowStorage';

import DestroyModal from 'components/application/DestroyModal';
import Paginator from 'components/application/Paginator';
import ResourceBlankNotice from 'components/application/ResourceBlankNotice';
import SuccessToast from 'components/application/SuccessToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import AdditionalDestroyModalContent from 'components/courses/AdditionalDestroyModalContent';
import CoursesBar from 'components/courses/CoursesBar';
import CoursesTable from 'components/courses/CoursesTable';
import CourseSidePanel from 'components/courses/sidepanel/CourseSidePanel';
import UpdateConfirmationModal from 'components/courses/UpdateConfirmationModal';

import {
  autoEnrolmentConfigurationParamsForCreate,
  autoEnrolmentConfigurationParamsForUpdate,
} from 'components/helpers/resources/autoEnrolments';
import {
  paramsForCreate as courseCompanyRolesParamsForCreate,
  paramsForUpdate as courseCompanyRolesParamsForUpdate,
} from 'components/helpers/resources/courseCompanyRoles';
import {
  asCourseObject,
  courseAsParams,
  defaultCourse,
  hasCourseBecomeELearning,
  hasCourseProviderTypeChanged,
} from 'components/helpers/resources/courses';

const defaultCourses = {
  loaded: false,
  collection: [],
  originalCourseCollection: {},
  eLearningCourseCollection: {},
  autoEnrolmentConfigurationCollection: {},
};

const defaultPersonnel = {
  loaded: false,
  collection: [],
  includedSubcontractors: [],
  includedBookings: [],
  includedRegistrations: [],
  includedTrainings: [],
  includedAutoEnrolmentExclusions: [],
  includedProfilePhotos: [],
  meta: { currentPage: 1, isLastPage: false },
};

const formatCourseCompanyRoles = (data) =>
  data.map((ccr) => ({
    companyRoleId: ccr.relationships.companyRole.data.id,
    required: ccr.attributes.required,
    requirementPriorityCount: ccr.meta.requirementPriorityCount,
  }));

const initialTabStore = {
  currentSearch: '',
  page: 1,
  currentResourceId: '',
  currentResourceName: '',
  sidePanelIsOpen: false,
};

const tabSchema = z.object({
  currentResourceId: z.string().nullable(),
  currentResourceName: z.string(),
  currentSearch: z.string(),
  page: z.number().or(z.null()),
  sidePanelIsOpen: z.boolean(),
});

export default function CoursesTab() {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();

  const trainingRegisterResourceManagementContext =
    useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.CoursesTab',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [getStore, setStore] = useWindowStorage(
    'trainingRegister|courseRegister',
    { store: window.sessionStorage },
  );

  const tabStore = useValidatedStore({
    getStore,
    initialStore: initialTabStore,
    schema: tabSchema,
  });

  const initialCurrentSearch = { coursesSearch: tabStore.currentSearch };

  const initialDefaultCourse = {
    ...defaultCourse,
    id: tabStore.currentResourceId,
    name: tabStore.currentResourceName,
  };

  const isTabStoreCourseLoaded = useRef(false);
  const isTabStoreSidePanelOpen = useRef(tabStore.sidePanelIsOpen);

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [courses, setCourses] = useState(defaultCourses);

  const [
    sidePanelIsOpen,
    _setSidePanelIsOpen,
    openSidePanel,
    closeSidePanel,
    _resetSidePanelContext,
    sidePanelContext,
    setSidePanelContext,
  ] = useSidePanel(false, 'show');

  const [
    currentCourse,
    setCurrentCourse,
    handleCourseInputChange,
    handleCourseOptionChange,
  ] = useForm(initialDefaultCourse);

  const [currentSearch, setCurrentSearch, handleSearchInputChange] =
    useForm(initialCurrentSearch);

  const [debouncedCurrentSearch, _resetDebouncedCurrentSearch] = useDebounce(
    currentSearch,
    250,
  );

  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const [metaData, setMetaData] = useState({
    currentPage: null,
    totalPages: null,
    scopedCount: 0,
    totalCount: 0,
  });

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);

  const [isUpdateConfirmationModalOpen, setIsUpdateConfirmationModalOpen] =
    useState(false);

  const [isLoadMorePersonnelActive, setIsLoadMorePersonnelActive] =
    useState(false);

  const [personnel, setPersonnel] = useState(defaultPersonnel);

  const [assignableRoles, setAssignableRoles] = useState({
    loaded: false,
    collection: [],
  });

  const [courseCompanyRoles, setCourseCompanyRoles] = useState({
    loaded: false,
    collection: [],
  });

  // computed
  const domainCourse =
    selectedCourseId &&
    courses.collection.find((course) => course.id === selectedCourseId);

  const originalCourse =
    (domainCourse &&
      courses.originalCourseCollection[
        domainCourse.relationships.originalCourse.data?.id
      ]) ||
    null;

  const fetchAssignableRoles = function () {
    axios
      .get('/assignable_company_roles')
      .then((response) =>
        setAssignableRoles({ loaded: true, collection: response.data.data }),
      )
      .catch(handleRequestError);
  };

  const fetchCourseCompanyRoles = function () {
    axios
      .get(`/courses/${currentCourse.id}/course_company_roles`)
      .then((response) => {
        setCourseCompanyRoles({ loaded: true, collection: response.data });
        setCurrentCourse({
          ...currentCourse,
          courseCompanyRoles: formatCourseCompanyRoles(response.data.data),
        });
      })
      .catch(breadBoard.addInedibleToast);
  };

  const fetchPersonnel = ({ increment }) => {
    setIsLoadMorePersonnelActive(false);
    let page = parseInt(personnel.meta.currentPage, 10);
    if (increment) {
      page += 1;
    }
    axios
      .get(`/courses/${currentCourse.id}/personnel`, { params: { page } })
      .then((response) => {
        setPersonnel((prevState) => ({
          loaded: true,
          collection: prevState.collection.concat(response.data.data),
          includedSubcontractors: prevState.includedSubcontractors.concat(
            response.data.included.filter(
              (obj) => obj.type === 'subcontractor',
            ),
          ),
          includedBookings: prevState.includedBookings.concat(
            response.data.included.filter((obj) => obj.type === 'booking'),
          ),
          includedTrainings: prevState.includedTrainings.concat(
            response.data.included.filter((obj) => obj.type === 'training'),
          ),
          includedRegistrations: prevState.includedRegistrations.concat(
            response.data.included.filter((obj) => obj.type === 'registration'),
          ),
          includedProfilePhotos: prevState.includedProfilePhotos.concat(
            response.data.included.filter((obj) => obj.type === 'profilePhoto'),
          ),
          includedAutoEnrolmentExclusions:
            prevState.includedAutoEnrolmentExclusions.concat(
              response.data.included.filter(
                (obj) => obj.type === 'autoEnrolmentExclusion',
              ),
            ),
          meta: response.data.meta,
        }));
        setIsLoadMorePersonnelActive(true);
      })
      .catch(breadBoard.addInedibleToast);
  };

  const areBookingsDestroyedOnUpdate = () =>
    currentCourse.bookingCount > 0 &&
    hasCourseProviderTypeChanged(currentCourse, courses.collection);

  const refreshCourses = (page = 1) => {
    axios
      .get('/courses', {
        params: { search: currentSearch.coursesSearch, page },
      })
      .then((response) => {
        const courses = response.data.included.reduce(
          (accumulator, includedResource) => {
            accumulator[`${includedResource.type}Collection`][
              includedResource.id
            ] = includedResource;
            return accumulator;
          },
          { ...defaultCourses, loaded: true, collection: response.data.data },
        );

        setCourses(courses);
        setMetaData(response.data.meta);
      })
      .catch(breadBoard.addInedibleToast);
  };

  const { currencyCode } = currentActor.division.attributes;

  const createCurrentCourse = () => {
    const courseCompanyRolesParams = courseCompanyRolesParamsForCreate({
      currentId: currentCourse.id,
      frontendCourseCompanyRoles: currentCourse.courseCompanyRoles,
      currentType: 'course',
    });

    const autoEnrolmentConfigurationParams =
      (
        currentActor.isAllowedFeature([
          'e_learning_auto_enrol',
          'e_learning',
          'training_register',
        ])
      ) ?
        autoEnrolmentConfigurationParamsForCreate({
          currentCourse,
          currentContext: trainingRegisterResourceManagementContext,
        })
      : null;

    axios
      .post(
        '/courses',
        courseAsParams({
          course: currentCourse,
          courseCompanyRolesParams,
          autoEnrolmentConfigurationParams,
          eLearningCourses: courses.eLearningCourseCollection,
          currencyCode,
        }),
      )
      .then((response) => {
        resetCourses();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.added'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: response.data.data.attributes.name }}
              />
            }
          />,
        );
      })
      .catch(handleRequestError);
  };

  const confirmUpdateOrUpdateCurrentCourse = () => {
    areBookingsDestroyedOnUpdate() ?
      setIsUpdateConfirmationModalOpen(true)
    : updateCurrentCourse();
  };

  const updateCurrentCourse = () => {
    const courseCompanyRolesParams = courseCompanyRolesParamsForUpdate({
      currentId: currentCourse.id,
      serverCourseCompanyRoles: courseCompanyRoles.collection.data,
      frontendCourseCompanyRoles: currentCourse.courseCompanyRoles,
      currentType: 'course',
    });

    const autoEnrolmentConfigurationParams =
      (function determineAutoEnrolmentConfigurationParams() {
        if (
          currentActor.isAllowedFeature([
            'e_learning_auto_enrol',
            'e_learning',
            'training_register',
          ])
        ) {
          const domainAutoEnrolmentConfiguration =
            domainCourse?.relationships?.autoEnrolmentConfiguration?.data ?
              courses.autoEnrolmentConfigurationCollection[
                domainCourse.relationships.autoEnrolmentConfiguration.data?.id
              ]
            : null;

          return autoEnrolmentConfigurationParamsForUpdate({
            currentCourse,
            domainAutoEnrolmentConfiguration,
            currentContext: trainingRegisterResourceManagementContext,
          });
        } else {
          return null;
        }
      })();

    axios
      .patch(
        `/courses/${currentCourse.id}`,
        courseAsParams({
          course: currentCourse,
          courseCompanyRolesParams,
          autoEnrolmentConfigurationParams,
          eLearningCourses: courses.eLearningCourseCollection,
          currencyCode,
        }),
      )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: ['courseOverrides', currentCourse.id],
        });

        areBookingsDestroyedOnUpdate() ?
          destroyCurrentCourseBookings()
        : resetCourses();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.updated'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: response.data.data.attributes.name }}
              />
            }
          />,
        );
      })
      .catch(handleRequestError);
  };

  const destroyCurrentCourseBookings = () => {
    axios
      .post(`/courses/${currentCourse.id}/bookings_bulk_destroy`)
      .then(() => resetCourses())
      .catch(breadBoard.addInedibleToast);
  };

  const destroyCurrentCourse = () => {
    axios
      .delete(`/courses/${currentCourse.id}`)
      .then(() => {
        resetCourses();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.deleted'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: currentCourse.name }}
              />
            }
          />,
        );
      })
      .catch(handleRequestError);
  };

  const resetCourses = () => {
    queryClient.invalidateQueries(['assignableCourses']);
    queryClient.invalidateQueries(['bookings', 'assignableCourses']);
    queryClient.invalidateQueries(['companyRoles']);

    closeSidePanel();
    refreshCourses();
    setCurrentCourse(defaultCourse);
    resetRequestError();
  };

  const handleSearchReset = (event) => {
    const name = event.target.getAttribute('data-attr-name');
    setCurrentSearch({ ...currentSearch, [name]: '' });
  };

  const handlePageChange = (event) => {
    refreshCourses(event.currentTarget.getAttribute('data-page'));
  };

  const handleELearningProviderSelectedChange = ({ selected }) => {
    if (selected) {
      setCurrentCourse({
        ...currentCourse,
        isELearningProviderSelected: true,
        learningMethod: 'e_learning',
        provider: 'iHasco',
      });
    } else {
      setCurrentCourse({
        ...currentCourse,
        isELearningProviderSelected: false,
        learningMethod: '',
        provider: '',
      });
    }
  };

  const closePanel = () => {
    closeSidePanel();
    resetRequestError();
  };

  const viewCourse = (course, originalCourse) => {
    resetRequestError();
    setSelectedCourseId(course.id);

    const autoEnrolmentConfiguration =
      (
        currentActor.isAllowedFeature([
          'e_learning_auto_enrol',
          'e_learning',
          'training_register',
        ])
      ) ?
        courses.autoEnrolmentConfigurationCollection[
          course.relationships.autoEnrolmentConfiguration.data?.id
        ]
      : null;

    setCurrentCourse(
      asCourseObject({
        course,
        originalCourse,
        autoEnrolmentConfiguration,
        eLearningAllowed: currentActor.isAllowedFeature('e_learning'),
        currencyCode,
      }),
    );
    setSidePanelContext('show');
    openSidePanel();
  };

  const handleCourseRolesOptionChange = (roleId) => {
    const newCourseCompanyRoles = [
      ...currentCourse.courseCompanyRoles,
      { companyRoleId: roleId, required: true },
    ];
    setCurrentCourse({
      ...currentCourse,
      courseCompanyRoles: newCourseCompanyRoles,
    });
  };

  const handleCourseCompanyRoleRequiredOnChange = (roleId, value) => {
    const newCourseCompanyRoles = [...currentCourse.courseCompanyRoles];
    const courseCompanyRoleIndex = newCourseCompanyRoles.findIndex(
      (ccr) => ccr.companyRoleId === roleId,
    );
    newCourseCompanyRoles[courseCompanyRoleIndex] = {
      ...newCourseCompanyRoles[courseCompanyRoleIndex],
      required: value,
    };
    setCurrentCourse({
      ...currentCourse,
      courseCompanyRoles: newCourseCompanyRoles,
    });
  };

  const handleCourseCompanyRoleOnDelete = (roleId) => {
    const newCourseCompanyRoles = currentCourse.courseCompanyRoles.filter(
      (ccr) => ccr.companyRoleId !== roleId,
    );
    setCurrentCourse({
      ...currentCourse,
      courseCompanyRoles: newCourseCompanyRoles,
    });
  };

  if (
    tabStore.currentResourceId &&
    !isTabStoreCourseLoaded.current &&
    isTabStoreSidePanelOpen.current &&
    courses.loaded &&
    courses.collection.length !== 0
  ) {
    const tabStoreCourse = courses.collection.find(
      (course) => course.id === tabStore.currentResourceId,
    );

    if (tabStoreCourse) {
      const originalCourseData =
        tabStoreCourse.relationships.originalCourse.data;
      const originalCourse =
        courses.originalCourseCollection[(originalCourseData || {}).id];
      viewCourse(tabStoreCourse, originalCourse);
    }

    isTabStoreCourseLoaded.current = true;
  }

  useEffect(() => {
    refreshCourses(tabStore.page);
  }, []);

  useEffect(() => {
    if (courses.loaded && debouncedCurrentSearch) refreshCourses();
  }, [debouncedCurrentSearch]);

  useEffect(() => {
    // fetch roles only in these conditions to avoid request spamming
    if (!assignableRoles.loaded && sidePanelIsOpen) {
      fetchAssignableRoles();
    }

    if (currentCourse.id && sidePanelIsOpen) {
      fetchCourseCompanyRoles();
      fetchPersonnel({ increment: false });
    }

    if (!sidePanelIsOpen) {
      setPersonnel(defaultPersonnel);
      setIsLoadMorePersonnelActive(false);
    }
  }, [sidePanelIsOpen]);

  const [loadMoreRef, loadMoreInView] = useInView();

  useEffect(() => {
    if (currentCourse.id && !personnel.meta.isLastPage && loadMoreInView)
      fetchPersonnel({ increment: true });
  }, [currentCourse.id, personnel.meta.isLastPage, loadMoreInView]);

  useEffect(() => {
    setStore({
      currentSearch: currentSearch.coursesSearch,
      page: metaData.currentPage,
      currentResourceId: currentCourse.id,
      currentResourceName: currentCourse.name,
      sidePanelIsOpen,
    });
  }, [
    currentSearch.coursesSearch,
    metaData.currentPage,
    currentCourse.id,
    currentCourse.name,
    sidePanelIsOpen,
  ]);

  return (
    courses.loaded && (
      <>
        <CoursesBar
          actionButtonsVisible={
            trainingRegisterResourceManagementContext.hasCourseEditableAccess
          }
          coursesCount={metaData.totalCount}
          coursesSearch={currentSearch.coursesSearch}
          onSearchInputChange={handleSearchInputChange}
          onSearchReset={handleSearchReset}
          openSidePanel={openSidePanel}
          resetRequestError={resetRequestError}
          setCurrentCourse={setCurrentCourse}
          setSidePanelContext={setSidePanelContext}
        />
        {courses.collection.length > 0 ?
          <>
            <CoursesTable
              courses={courses.collection}
              onRowClick={viewCourse}
              originalCourses={courses.originalCourseCollection}
            />
            {courses.loaded &&
              courses.collection.length > 0 &&
              metaData.totalPages > 1 && (
                <div className='tw-mt-20 tw-text-center'>
                  <Paginator
                    currentPage={metaData.currentPage}
                    onClick={handlePageChange}
                    totalPages={metaData.totalPages}
                  />
                </div>
              )}
          </>
        : <ResourceBlankNotice
            icon={
              <AddCourseIcon
                className='-tw-mb-2 [&_path]:tw-fill-grey-500'
                height={64}
                width={64}
              />
            }
            noResourcesDescription={i18nT(
              `blankNotice.noResourcesDescription.${trainingRegisterResourceManagementContext.hasCourseEditableAccess ? 'editable' : 'readOnly'}`,
            )}
            noResourcesFoundTitle={i18nT('blankNotice.noResourcesFoundTitle')}
            noResourcesTitle={i18nT(
              `blankNotice.noResourcesTitle.${trainingRegisterResourceManagementContext.hasCourseEditableAccess ? 'editable' : 'readOnly'}`,
            )}
            totalCount={metaData.totalCount}
          />
        }
        <CourseSidePanel
          allowShowFooterContents={
            trainingRegisterResourceManagementContext.hasCourseEditableAccess
          }
          assignableRoles={assignableRoles.collection}
          autoEnrolmentConfiguration={
            courses.autoEnrolmentConfigurationCollection[
              currentCourse && currentCourse.autoEnrolmentConfigurationId
            ]
          }
          closeCallback={closePanel}
          createCourse={createCurrentCourse}
          currentCourse={currentCourse}
          domainCourse={domainCourse}
          eLearningCourse={
            courses.eLearningCourseCollection[
              currentCourse && currentCourse.eLearningCourseId
            ]
          }
          isLoadMorePersonnelActive={isLoadMorePersonnelActive}
          isOpen={sidePanelIsOpen}
          loadMoreRef={loadMoreRef}
          onCourseCompanyRoleDelete={handleCourseCompanyRoleOnDelete}
          onCourseCompanyRoleRequiredChange={
            handleCourseCompanyRoleRequiredOnChange
          }
          onCourseRolesOptionChange={handleCourseRolesOptionChange}
          onCoursesReset={resetCourses}
          onDeleteClick={() => setIsDestroyModalOpen(true)}
          onELearningProviderSelectedChange={
            handleELearningProviderSelectedChange
          }
          onInputChange={handleCourseInputChange}
          onOptionChange={handleCourseOptionChange}
          onRequestError={handleRequestError}
          originalCourse={originalCourse}
          personnel={personnel}
          removeErrorStyling={removeErrorStyling}
          requestError={requestError}
          setSidePanelContext={setSidePanelContext}
          sidePanelContext={sidePanelContext}
          submitDisabled={submitDisabled}
          updateCourse={confirmUpdateOrUpdateCurrentCourse}
        />
        {sidePanelContext === 'show' && (
          <DestroyModal
            additionalContent={
              <AdditionalDestroyModalContent
                hasELearningBookings={
                  currentCourse.isELearningProviderSelected &&
                  currentCourse.bookingCount > 0
                }
              />
            }
            confirmationText={i18nT('destroyModal.confirmationText')}
            displayText={i18nTShared('areYouSureYouWantToDelete', {
              interpolation: { escapeValue: false },
              name: currentCourse.name,
            })}
            isOpen={isDestroyModalOpen}
            onClose={() => setIsDestroyModalOpen(false)}
            onDestroy={destroyCurrentCourse}
          />
        )}
        {sidePanelContext === 'edit' && (
          <UpdateConfirmationModal
            displayText={i18nT(
              `updateConfirmationModal.displayText.${hasCourseBecomeELearning(currentCourse, courses.collection) ? 'default' : 'eLearning'}`,
            )}
            isOpen={isUpdateConfirmationModalOpen}
            onClose={() => setIsUpdateConfirmationModalOpen(false)}
            onSave={updateCurrentCourse}
          />
        )}
      </>
    )
  );
}
